import React, { useEffect, useState } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { loginRequest } from '../services/authConfig';
import { useNavigate, useLocation } from 'react-router-dom';
import guardAuthService from '../services/guardAuth.service';
import '../styles/Login.css';

function Login() {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const navigate = useNavigate();
  const location = useLocation();
  const [loginError, setLoginError] = useState<string>('');

  const [isDark, setIsDark] = useState<boolean>(() => document.documentElement.classList.contains('dark'));
  useEffect(() => {
    const check = () => setIsDark(document.documentElement.classList.contains('dark'));
    check();
    const observer = new MutationObserver(() => check());
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // Guard login state
  const [loginMode, setLoginMode] = useState<'employee' | 'guard'>('employee');
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [guardError, setGuardError] = useState<string>('');
  const [guardLoading, setGuardLoading] = useState<boolean>(false);

  // MFA challenge state — populated after password succeeds.
  const [mfaChallenge, setMfaChallenge] = useState<string | null>(null);
  const [mfaPhoneHint, setMfaPhoneHint] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState<string>('');

  // Check if we were redirected due to session expiration
  const searchParams = new URLSearchParams(location.search);
  const sessionExpired = searchParams.get('session_expired') === 'true';
  // Set when an admin force-signed-out this user (Administration > Users). Same
  // no-auto-redirect handling as an expired session — only the message differs,
  // so they know it wasn't a glitch and that signing back in is expected.
  const forceLoggedOut = searchParams.get('force_logout') === 'true';

  // Get the intended destination from location state (set by PrivateRoute)
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/dashboard';

  useEffect(() => {
    // If session expired, don't auto-redirect even if MSAL thinks we're authenticated
    // The user needs to sign in again with fresh tokens
    if (sessionExpired || forceLoggedOut) {
      // Clear the redirect tracking flag
      sessionStorage.removeItem('lastAuthRedirect');
      return;
    }

    // Check if already authenticated - redirect to intended destination
    if (isAuthenticated && accounts.length > 0) {
      navigate(from, { replace: true });
      return;
    }

    // Guard already authenticated - bounce to yard home
    if (guardAuthService.isAuthenticated()) {
      navigate('/yard/inventory', { replace: true });
    }
  }, [isAuthenticated, accounts, navigate, from, sessionExpired, forceLoggedOut]);

  const handleLogin = async () => {
    setLoginError('');

    // Wait for any ongoing interactions to complete
    if (inProgress !== 'none') {
      return;
    }

    try {
      // Use redirect login instead of popup
      await instance.loginRedirect({
        ...loginRequest,
        prompt: 'select_account'
      });
    } catch (error: any) {
      // Set user-friendly error message
      let errorMessage = 'Login failed. ';

      // Handle specific error cases
      if (error?.errorCode === 'user_cancelled') {
        errorMessage = 'Login was cancelled.';
      } else if (error?.errorCode === 'consent_required') {
        errorMessage = 'Additional consent is required. Please try again.';
      } else {
        errorMessage += error?.errorMessage || error?.message || 'Please try again.';
      }

      setLoginError(errorMessage);
    }
  };

  const handleGuardLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setGuardError('');
    setGuardLoading(true);

    try {
      const result = await guardAuthService.login({ username, password });
      if (result.mfa_required && result.mfa_challenge_token) {
        setMfaChallenge(result.mfa_challenge_token);
        setMfaPhoneHint(result.phone_last_four ?? null);
        setMfaCode('');
        setGuardLoading(false);
        return;
      }
      // MFA is mandatory, so a no-MFA response is unexpected — treat as an error.
      setGuardError('Unexpected login response. Please contact your administrator.');
    } catch (error: any) {
      const errorMessage = error?.response?.data?.detail || error?.message || 'Login failed. Please try again.';
      setGuardError(errorMessage);
    } finally {
      setGuardLoading(false);
    }
  };

  const handleVerifyMfa = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaChallenge) return;
    setGuardError('');
    setGuardLoading(true);
    try {
      const result = await guardAuthService.verifyMfa(username, {
        challenge_token: mfaChallenge,
        code: mfaCode,
      });
      // Hard-navigate so the guard /yard/* route tree registers before
      // we land on a yard URL — avoids a one-render window where react-router
      // would otherwise fall through to DashboardLayout's employee gate.
      const target = result.must_change_password ? '/yard/change-password' : '/yard/inventory';
      window.location.assign(target);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.detail || error?.message || 'Verification failed. Please try again.';
      setGuardError(errorMessage);
      // On "too many attempts" message from the server, drop back to the username/password step.
      if (typeof errorMessage === 'string' && errorMessage.toLowerCase().includes('please log in again')) {
        setMfaChallenge(null);
        setMfaCode('');
      }
      setGuardLoading(false);
    }
  };

  const handleCancelMfa = () => {
    setMfaChallenge(null);
    setMfaCode('');
    setMfaPhoneHint(null);
    setGuardError('');
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="login-logo">
          <h1>gwi360</h1>
          <p>Container Management System</p>
        </div>

        <div className="login-content">
          <h2>Welcome</h2>

          {/* Login Mode Tabs */}
          <div className="login-tabs">
            <button
              className={`login-tab ${loginMode === 'employee' ? 'active' : ''}`}
              onClick={() => setLoginMode('employee')}
            >
              Employee
            </button>
            <button
              className={`login-tab ${loginMode === 'guard' ? 'active' : ''}`}
              onClick={() => setLoginMode('guard')}
            >
              Other
            </button>
          </div>

          {loginMode === 'employee' ? (
            <>
              <p>Sign in with your organizational account to continue</p>

              <button onClick={handleLogin} className="login-button">
                <svg className="microsoft-logo" viewBox="0 0 23 23" xmlns="http://www.w3.org/2000/svg">
                  <rect x="1" y="1" width="10" height="10" fill="#f25022"/>
                  <rect x="12" y="1" width="10" height="10" fill="#00a4ef"/>
                  <rect x="1" y="12" width="10" height="10" fill="#7fba00"/>
                  <rect x="12" y="12" width="10" height="10" fill="#ffb900"/>
                </svg>
                Sign in with Microsoft
              </button>

              {forceLoggedOut && !loginError && (
                <div style={{marginTop: '20px', padding: '10px', borderRadius: '4px', ...(isDark ? {background: 'rgba(234,179,8,0.15)', border: '1px solid #ca8a04', color: '#fde68a'} : {background: '#fff3cd', border: '1px solid #ffc107', color: '#856404'})}}>
                  An administrator signed you out. Please sign in again.
                </div>
              )}

              {sessionExpired && !forceLoggedOut && !loginError && (
                <div style={{marginTop: '20px', padding: '10px', borderRadius: '4px', ...(isDark ? {background: 'rgba(234,179,8,0.15)', border: '1px solid #ca8a04', color: '#fde68a'} : {background: '#fff3cd', border: '1px solid #ffc107', color: '#856404'})}}>
                  Your session has expired. Please sign in again.
                </div>
              )}

              {loginError && (
                <div style={{marginTop: '20px', padding: '10px', borderRadius: '4px', ...(isDark ? {background: 'rgba(220,38,38,0.15)', border: '1px solid #ef4444', color: '#fca5a5'} : {background: '#fee', border: '1px solid #fcc', color: '#c00'})}}>
                  {loginError}
                </div>
              )}
            </>
          ) : mfaChallenge ? (
            <>
              <p>
                Enter the 6-digit code sent to
                {mfaPhoneHint ? ` ***${mfaPhoneHint}` : ' your phone'}.
              </p>

              <form onSubmit={handleVerifyMfa} className="guard-login-form">
                <div className="form-group">
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="\d{6}"
                    maxLength={6}
                    placeholder="123456"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                    className="login-input"
                    required
                    autoFocus
                    disabled={guardLoading}
                  />
                </div>
                <button type="submit" className="login-button guard-login-button" disabled={guardLoading || mfaCode.length !== 6}>
                  {guardLoading ? 'Verifying...' : 'Verify'}
                </button>
                <button
                  type="button"
                  onClick={handleCancelMfa}
                  className="login-button"
                  style={{ marginTop: '10px', background: 'transparent', color: isDark ? '#d1d5db' : '#555', border: isDark ? '1px solid #4b5563' : '1px solid #ccc' }}
                  disabled={guardLoading}
                >
                  Cancel
                </button>
              </form>

              {guardError && (
                <div style={{marginTop: '20px', padding: '10px', borderRadius: '4px', ...(isDark ? {background: 'rgba(220,38,38,0.15)', border: '1px solid #ef4444', color: '#fca5a5'} : {background: '#fee', border: '1px solid #fcc', color: '#c00'})}}>
                  {guardError}
                </div>
              )}
            </>
          ) : (
            <>
              <p>Sign in with your guard credentials</p>

              <form onSubmit={handleGuardLogin} className="guard-login-form">
                <div className="form-group">
                  <input
                    type="text"
                    placeholder="Username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="login-input"
                    required
                    disabled={guardLoading}
                  />
                </div>
                <div className="form-group">
                  <input
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="login-input"
                    required
                    disabled={guardLoading}
                  />
                </div>
                <button type="submit" className="login-button guard-login-button" disabled={guardLoading}>
                  {guardLoading ? 'Signing in...' : 'Sign In'}
                </button>
              </form>

              {guardError && (
                <div style={{marginTop: '20px', padding: '10px', borderRadius: '4px', ...(isDark ? {background: 'rgba(220,38,38,0.15)', border: '1px solid #ef4444', color: '#fca5a5'} : {background: '#fee', border: '1px solid #fcc', color: '#c00'})}}>
                  {guardError}
                </div>
              )}
            </>
          )}
        </div>

        <div className="login-footer">
          {loginMode === 'employee' ? (
            <>
              <p>Powered by Microsoft Azure</p>
              <p className="security-note">Your sign-in is secured with Azure Active Directory</p>
            </>
          ) : (
            <p className="security-note">Sign in with your credentials</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default Login;